import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BellRingingIcon,
  ChatCircleDotsIcon,
  TelegramLogoIcon,
  WarningIcon,
  type Icon,
} from '@phosphor-icons/react';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatusChip,
  cn,
  fmtRelativeTime,
  type Tone,
} from '@smena/ui';
import {
  OPS_NOTIFY_TRANSPORTS,
  chatTypeLabel,
  fetchOperatorNotify,
  issueBindCode,
  sendOperatorTest,
  unbindOperatorChat,
  type IssuedBindCode,
  type OperatorNotifyStatus,
  type OpsNotifyTransport,
} from './api';

/**
 * «Оповещения оператора»: куда падают эскалации, когда агент зовёт человека.
 *
 * Карточка существует ради неподключённого состояния. Пока чат не привязан, эскалация
 * молча оседает в очереди - гость ждёт человека, а человек не знает, что его ждут.
 * Раньше это состояние вообще нигде не было видно: чат задавался переменной окружения
 * на стороне вендора, и УК не мог ни узнать о проблеме, ни решить её сам.
 *
 * Транспорта два (Telegram и MAX) и они независимы: свой бот, свой код, свой чат. Поэтому
 * не переключатель, а две секции сразу: подключённый Telegram не должен прятать то, что
 * MAX не подключён, - иначе «зелёная» карточка снова скрывает половину правды.
 */
export function OperatorNotifyCard() {
  const telegram = useTransportStatus('telegram');
  const max = useTransportStatus('max');

  const status: Record<OpsNotifyTransport, OperatorNotifyStatus | undefined> = {
    telegram: telegram.data,
    max: max.data,
  };
  const loaded = Boolean(telegram.data ?? max.data);
  const connected = OPS_NOTIFY_TRANSPORTS.filter((t) => status[t]?.connected).map((t) => META[t].label);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <BellRingingIcon weight="bold" className="size-4 text-accent-fg" /> Оповещения оператора
          </CardTitle>
          <p className="mt-1 text-xs text-muted">
            Куда приходят эскалации, когда агент зовёт человека. Транспорта два и они независимы -
            можно подключить любой или оба сразу.
          </p>
        </div>
        {loaded ? (
          <StatusChip tone={connected.length > 0 ? 'green' : 'amber'} className="shrink-0">
            {connected.length > 0 ? `подключено: ${connected.join(', ')}` : 'не подключено'}
          </StatusChip>
        ) : null}
      </CardHeader>

      <CardContent className="flex flex-col gap-3 text-sm">
        {/* Тревога одна на всю карточку: важно не то, какой транспорт молчит, а то, что молчат оба. */}
        {loaded && connected.length === 0 ? (
          <div className="flex items-start gap-2 rounded-md border border-amber/30 bg-amber/10 p-3 text-xs text-amber">
            <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
            <span>
              Пока не подключён ни один чат, эскалации <b>никуда не приходят</b>: когда агент зовёт
              человека, гость ждёт ответа, а вы об этом не узнаете.
            </span>
          </div>
        ) : null}

        {OPS_NOTIFY_TRANSPORTS.map((t) => (
          <TransportSection key={t} transport={t} status={status[t]} />
        ))}
      </CardContent>
    </Card>
  );
}

function useTransportStatus(transport: OpsNotifyTransport) {
  return useQuery({
    queryKey: ['ops-notify', transport],
    queryFn: () => fetchOperatorNotify(transport),
    // Пока идёт подключение, человек уходит в мессенджер и возвращается - статус должен
    // догнать сам, без F5. Ждать нечего, если чат уже привязан или бота нет вовсе.
    refetchInterval: (query) => {
      const s = query.state.data;
      return s?.botRunning && !s.connected ? 5_000 : 60_000;
    },
  });
}

interface TransportMeta {
  label: string;
  icon: Icon;
  /** Цвет канала тот же, что в списке диалогов: транспорт узнаётся по пятну, а не по чтению. */
  color: string;
  /** Чем подключение этого транспорта отличается - одной строкой, до выдачи кода. */
  hint: string;
  /**
   * Тон сообщения о неподнятом боте. У Telegram это аномалия - базовый транспорт оповещений
   * обязан работать. У MAX - штатная конфигурация: без `MAX_OPERATOR_BOT_TOKEN` канал просто
   * не поднимается, и красить это в тревогу значит звать в поддержку по поводу опции.
   */
  missingBot: { tone: 'warn' | 'note'; text: string };
}

const META: Record<OpsNotifyTransport, TransportMeta> = {
  telegram: {
    label: 'Telegram',
    icon: TelegramLogoIcon,
    color: 'text-tg',
    hint: 'В личный чат - кнопкой, в чат команды - кодом.',
    missingBot: {
      tone: 'warn',
      text: 'Бот оповещений не запущен на нашей стороне. Напишите в поддержку Smena - подключить самостоятельно не получится.',
    },
  },
  max: {
    label: 'MAX',
    icon: ChatCircleDotsIcon,
    color: 'text-max',
    hint: 'В личный чат - кнопкой. В чат команды MAX ботов пока не пускает.',
    missingBot: {
      tone: 'note',
      text: 'Бот MAX на этом стенде не поднят, канал недоступен. Оповещения в MAX - опция; включает поддержка Smena.',
    },
  },
};

/** Одна секция = один транспорт со своим состоянием, своим кодом и своими тремя действиями. */
function TransportSection({
  transport,
  status,
}: {
  transport: OpsNotifyTransport;
  status: OperatorNotifyStatus | undefined;
}) {
  const qc = useQueryClient();
  const meta = META[transport];
  const TransportIcon = meta.icon;
  const [issued, setIssued] = useState<IssuedBindCode | null>(null);

  const codeMut = useMutation({
    mutationFn: () => issueBindCode(transport),
    onSuccess: setIssued,
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось получить код'),
  });

  const testMut = useMutation({
    mutationFn: () => sendOperatorTest(transport),
    onSuccess: () => toast.success(`Отправили. Проверьте чат в ${meta.label}`),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не дошло'),
  });

  const unbindMut = useMutation({
    mutationFn: () => unbindOperatorChat(transport),
    onSuccess: () => {
      setIssued(null);
      toast.success(`Чат ${meta.label} отвязан`);
      void qc.invalidateQueries({ queryKey: ['ops-notify', transport] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось отвязать'),
  });

  const connected = Boolean(status?.connected);

  // Код больше не нужен, как только чат привязался.
  if (connected && issued) setIssued(null);

  let chip: { tone: Tone; text: string } = { tone: 'amber', text: 'не подключено' };
  if (status && !status.botRunning) chip = { tone: 'muted', text: 'недоступно' };
  else if (connected) chip = { tone: 'green', text: 'подключено' };

  let body: ReactNode;
  if (!status) {
    body = <p className="text-muted">Загружаем…</p>;
  } else if (!status.botRunning) {
    body =
      meta.missingBot.tone === 'warn' ? (
        <div className="flex items-start gap-2 rounded-md border border-amber/30 bg-amber/10 p-3 text-xs text-amber">
          <WarningIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
          <span>{meta.missingBot.text}</span>
        </div>
      ) : (
        <p className="text-xs text-muted">{meta.missingBot.text}</p>
      );
  } else if (connected) {
    body = (
      <>
        <p className="text-muted">
          Эскалации приходят в {chatTypeLabel(status.chatType)}
          {status.chatTitle ? <> «<span className="text-text">{status.chatTitle}</span>»</> : null}.
          {status.linkedAt ? <> Подключён {fmtRelativeTime(status.linkedAt)}.</> : null}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={testMut.isPending}
            onClick={() => testMut.mutate()}
          >
            {testMut.isPending ? 'Отправляем…' : 'Отправить тестовое'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={unbindMut.isPending}
            onClick={() => {
              // Отвязка гасит канал эскалаций молча - переспрашиваем, как в остальных карточках.
              if (confirm(`Отвязать чат ${meta.label}? Эскалации перестанут туда приходить.`)) {
                unbindMut.mutate();
              }
            }}
          >
            {unbindMut.isPending ? 'Отвязываем…' : 'Отвязать'}
          </Button>
        </div>
        {/* Бота могли выкинуть из чата - статус об этом не знает, узнать можно только отправкой. */}
        <p className="text-xs text-muted">
          Если бота удалили из чата, статус останется зелёным - проверяйте тестовым сообщением.
        </p>
      </>
    );
  } else {
    body = (
      <>
        <p className="text-xs text-muted">{meta.hint}</p>
        {!issued ? (
          <Button
            size="sm"
            className="self-start"
            disabled={codeMut.isPending}
            onClick={() => codeMut.mutate()}
          >
            {codeMut.isPending ? 'Готовим…' : 'Подключить'}
          </Button>
        ) : (
          <BindInstructions issued={issued} transport={transport} botUsername={status.botUsername} />
        )}
      </>
    );
  }

  return (
    <section className="rounded-md border border-line bg-panel2/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-medium">
          <TransportIcon weight="bold" className={cn('size-4', meta.color)} />
          {meta.label}
        </div>
        <StatusChip tone={chip.tone}>{chip.text}</StatusChip>
      </div>
      <div className="mt-2 flex flex-col gap-2">{body}</div>
    </section>
  );
}

function BindInstructions({
  issued,
  transport,
  botUsername,
}: {
  issued: IssuedBindCode;
  transport: OpsNotifyTransport;
  botUsername: string | null;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-line bg-panel p-3">
      {transport === 'telegram' ? (
        <TelegramSteps issued={issued} />
      ) : (
        <MaxSteps issued={issued} botUsername={botUsername} />
      )}

      <div className="flex items-baseline justify-between gap-3 border-t border-line pt-3">
        <span className="text-xs text-muted">Код действует 15 минут</span>
        <code className="font-mono text-base tracking-[0.2em] text-text">{issued.code}</code>
      </div>
    </div>
  );
}

/** Два пути: личный чат кнопкой и чат команды кодом - в группе deep-link не срабатывает. */
function TelegramSteps({ issued }: { issued: IssuedBindCode }) {
  return (
    <>
      <div>
        <div className="text-xs uppercase tracking-wider text-muted">Себе в личный чат</div>
        <div className="mt-1.5">
          {issued.deepLink ? (
            <Button size="sm" asChild>
              <a href={issued.deepLink} target="_blank" rel="noreferrer">
                <TelegramLogoIcon weight="bold" className="size-4" /> Открыть бота и подключить
              </a>
            </Button>
          ) : (
            <p className="text-xs text-muted">
              Ссылка недоступна - воспользуйтесь способом для чата команды.
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-line pt-3">
        <div className="text-xs uppercase tracking-wider text-muted">В чат команды</div>
        <ol className="mt-1.5 list-inside list-decimal space-y-1 text-xs text-muted">
          <li>Добавьте бота в нужный чат</li>
          <li>
            Отправьте туда сообщение <BindCommand code={issued.code} />
          </li>
        </ol>
      </div>
    </>
  );
}

/**
 * Подключение MAX - кнопкой, как в Telegram: `https://max.ru/<bot>?start=<код>` открывает
 * диалог с ботом и приносит код в `bot_started`, бот связывает чат с учёткой сам.
 *
 * Чат команды не предлагаем: MAX не пускает ботов в групповые чаты, и шаг «добавьте бота
 * в чат» отправлял бы человека делать невозможное.
 *
 * Код остаётся под кнопкой запасным путём - на случай, если ссылка откроется в браузере
 * или payload не доедет: тогда человек уже в нужном диалоге и просто отправит команду.
 */
function MaxSteps({ issued, botUsername }: { issued: IssuedBindCode; botUsername: string | null }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted">Себе в личный чат</div>
      <div className="mt-1.5">
        {issued.deepLink ? (
          <Button size="sm" asChild>
            <a href={issued.deepLink} target="_blank" rel="noreferrer">
              <ChatCircleDotsIcon weight="bold" className="size-4" /> Открыть MAX и подключить
            </a>
          </Button>
        ) : (
          <p className="text-xs text-muted">
            Ссылка недоступна: имя бота MAX не получено. Отправьте боту команду ниже.
          </p>
        )}
      </div>
      <p className="mt-2 text-xs text-muted">
        Не подключилось - откройте в MAX бота{' '}
        {botUsername ? <span className="font-mono text-text">@{botUsername}</span> : 'оповещений Smena'}{' '}
        и отправьте <BindCommand code={issued.code} />
      </p>
    </div>
  );
}

function BindCommand({ code }: { code: string }) {
  return (
    <code className="rounded bg-panel2 px-1 py-0.5 font-mono text-text">/bind {code}</code>
  );
}
